export type HighlightView = "left" | "right";

export interface HighlightImageDetails {
  imageUrl: string;
  linkUrl: string;
  kind: string;
  view: HighlightView;
  originalImageUrl: string;
}

const DEFAULT_HIGHLIGHT_KIND = "link";
const DEFAULT_HIGHLIGHT_VIEW: HighlightView = "left";

export function parseHighlightImageDetails(
  details: string | null | undefined
): HighlightImageDetails {
  const parts = String(details ?? "")
    .split(",")
    .map((part) => part.trim());

  const imageUrl = parts[0] || "";
  const linkUrl = parts[1] || "";
  const lastPart = parts[parts.length - 1];
  const view: HighlightView =
    lastPart === "right" || lastPart === "left"
      ? lastPart
      : DEFAULT_HIGHLIGHT_VIEW;

  const kindCandidate = parts[2];
  const kind =
    kindCandidate && kindCandidate !== "left" && kindCandidate !== "right"
      ? kindCandidate
      : DEFAULT_HIGHLIGHT_KIND;

  const originalImageUrl =
    parts.length >= 5 && view === lastPart ? parts[parts.length - 2] || "" : "";

  return {
    imageUrl,
    linkUrl,
    kind,
    view,
    originalImageUrl,
  };
}

export function buildHighlightImageDetails({
  imageUrl,
  linkUrl,
  kind = DEFAULT_HIGHLIGHT_KIND,
  view = DEFAULT_HIGHLIGHT_VIEW,
}: Partial<HighlightImageDetails> & {
  imageUrl: string;
  linkUrl: string;
}) {
  const details = [
    String(imageUrl || "").trim(),
    String(linkUrl || "").trim(),
    String(kind || DEFAULT_HIGHLIGHT_KIND).trim(),
  ];

  details.push(view === "right" ? "right" : "left");
  return details.join(",");
}

export function getHighlightImageUrl(details: string | null | undefined) {
  return parseHighlightImageDetails(details).imageUrl;
}

export function getHighlightOriginalImageUrl(details: string | null | undefined) {
  return parseHighlightImageDetails(details).originalImageUrl;
}

export function getHighlightView(details: string | null | undefined) {
  return parseHighlightImageDetails(details).view;
}

export function getHighlightOriginalSource(component: {
  details?: string | null;
  original_banner_image?: string | null;
}) {
  const explicit = String(component?.original_banner_image ?? "").trim();
  if (explicit) {
    return explicit;
  }

  return getHighlightOriginalImageUrl(component?.details);
}

export function hasHighlightCropDimensions(component: {
  details?: string | null;
  original_banner_image?: string | null;
}) {
  const displayUrl = getHighlightImageUrl(component?.details);
  const originalUrl = getHighlightOriginalSource(component);

  return Boolean(displayUrl && originalUrl && displayUrl !== originalUrl);
}
